import { useCallback, useEffect, useState } from "react";
import { TypedDocumentString } from "src/graphql/generated/graphql";
import { graphqlFetch } from "src/graphql/utils/graphqlFetch";

import { handleError } from "../../../../utils/error";

// Per-destination delivered-review count, derived from Campaign.reviewDeliveries — the products
// table's shipped index, paginated as {destination, reviewId} pairs. We page to completion and
// dedup by reviewId per destination (per-language copies of the same review→destination collapse).
// This is the real shipped signal, replacing the old submittedContents approximation for the
// e-shop funnel bars. Paginated on purpose: pre-aggregating server-side truncated large campaigns
// at DynamoDB's 1 MB page limit.
export interface ReviewDeliveryCount {
    destination?: string | null;
    count?: number | null;
}

interface ReviewDelivery {
    destination?: string | null;
    reviewId?: string | null;
}

interface ReviewDeliveriesResult {
    campaign?: {
        id?: string | null;
        reviewDeliveries?: {
            items?: Array<ReviewDelivery | null> | null;
            nextToken?: string | null;
        } | null;
    } | null;
}

// A `type` (not `interface`) so it satisfies graphqlFetch's `Record<string, unknown>` bound.
type ReviewDeliveriesVariables = {
    campaignId: string;
    nextToken?: string | null;
};

// Hand-authored (not codegen'd): the field is new and codegen can't run against a backend that
// hasn't deployed it yet. Mirrors the reportReviews paging pattern.
const reviewDeliveriesQuery = new TypedDocumentString<ReviewDeliveriesResult, ReviewDeliveriesVariables>(/* GraphQL */ `
    query CampaignReviewDeliveries($campaignId: String!, $nextToken: String) {
        campaign(campaignId: $campaignId) {
            id
            reviewDeliveries(nextToken: $nextToken) {
                items {
                    destination
                    reviewId
                }
                nextToken
            }
        }
    }
`);

const MAX_PAGES = 60; // safety backstop so a paging bug can't loop forever

export const useCampaignReviewDeliveryCounts = (campaignId: string) => {
    const [deliveryCounts, setDeliveryCounts] = useState<ReviewDeliveryCount[]>([]);
    // Start loading when there's a campaign to fetch, so the report's global loading gate closes
    // on the first render instead of flashing empty sections before the effect fires.
    const [isLoadingDeliveryCounts, setIsLoadingDeliveryCounts] = useState(Boolean(campaignId));

    const fetchDeliveryCounts = useCallback(async (id: string) => {
        setIsLoadingDeliveryCounts(true);
        try {
            const reviewIdsByDestination = new Map<string, Set<string>>();
            let nextToken: string | null | undefined;
            let page = 0;
            do {
                const response = await graphqlFetch(reviewDeliveriesQuery, { campaignId: id, nextToken });
                const connection = response.campaign?.reviewDeliveries;
                for (const entry of connection?.items || []) {
                    if (!entry?.destination || !entry?.reviewId) {
                        continue;
                    }
                    if (!reviewIdsByDestination.has(entry.destination)) {
                        reviewIdsByDestination.set(entry.destination, new Set());
                    }
                    reviewIdsByDestination.get(entry.destination)?.add(entry.reviewId);
                }
                nextToken = connection?.nextToken;
                page += 1;
            } while (nextToken && page < MAX_PAGES);

            setDeliveryCounts(
                [...reviewIdsByDestination.entries()].map(([destination, reviewIds]) => ({
                    destination,
                    count: reviewIds.size,
                }))
            );
        } catch (err) {
            handleError(err, { silent: true, context: { campaignId: id } });
            setDeliveryCounts([]);
        } finally {
            setIsLoadingDeliveryCounts(false);
        }
    }, []);

    useEffect(() => {
        if (!campaignId) {
            setDeliveryCounts([]);
            return;
        }
        fetchDeliveryCounts(campaignId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId]);

    return { deliveryCounts, isLoadingDeliveryCounts };
};
